import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { edrEventsTableQuery, EMPTY_EDR_EVENT_FILTERS, today, type EdrEventFilterValues, type EdrEventWindow, type Period } from '../api/securitee'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { edrEventColumns } from './columns'
import { EdrEventsFilters } from './EdrEventsFilters'
import { PeriodFilter } from './DevicesPage'
import { EdrDroppedAlert } from './EdrDroppedAlert'
import { DeviceFilterChip } from './DeviceFilterChip'

/**
 * Securitee > EDR events - telemetria bruta trimisa de agent (`it_edr_events`:
 * Windows Event Log, `linux/auth`, `linux/audit`, `linux/packages`), din care
 * Wazuh scoate detectiile. Pe perioada (azi, pana la alegerea din calendar),
 * fiindca volumul e mare; fiecare rand are descrierea pe o linie (EdrEventText,
 * ca in cronologia detectiei). Ecran nou, numai in v4; acelasi drept ca Detections.
 */
export const TABLE_ID = 'securiteeEdrEvents'

/**
 * `device` + `day` / `around`: lista vine din legatura "EDR events on <zi>" sau
 * "EDR events around 09:00" (`?device=&around=`, fereastra o da API-ul).
 * `onClearDevice` / `onClearAround` scot aparatul / momentul din adresa.
 */
export function SecuriteeEdrEventsPage({ device = 0, day, around, onClearDevice, onClearAround }: { device?: number; day?: string; around?: string; onClearDevice?(): void; onClearAround?(): void } = {}) {
  const [period, setPeriod] = useState<Period>(() => {
    const first = day ?? around?.slice(0, 10) ?? today()
    return { from: first, to: first }
  })
  const [filters, setFilters] = useState<EdrEventFilterValues>(EMPTY_EDR_EVENT_FILTERS)
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 0, dir: 'desc' }, search: '' })
  const base = useMemo(() => edrEventColumns(), [])
  const orderColumn = base.find((c) => c.index === state.order.column && c.orderable) ?? base[0]!
  const query = useQuery({
    ...edrEventsTableQuery({ draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search, ...period }, filters, device, around),
    placeholderData: keepPreviousData,
  })

  // coada SOC pe "All companies": coloana "Company" (docs XDR 03, decizia 7)
  const allCompanies = query.data?.all_companies === true
  const columns = useMemo(() => edrEventColumns(allCompanies), [allCompanies])
  const facets = query.data?.facets ?? { channels: [], eventIds: [] }
  const span = query.data?.window

  // un filtru nou sau alta perioada schimba setul, deci pagina o ia de la capat
  const onFilters = (next: EdrEventFilterValues) => {
    setFilters(next)
    setState({ start: 0 })
  }
  const onPeriod = (next: Period) => {
    setPeriod(next)
    setState({ start: 0 })
  }

  return (
    <Content>
      <div id="content">
        <EdrDroppedAlert />
        {(device > 0 || span) && (
          <div className="xdr-filter-chips">
            {device > 0 && <DeviceFilterChip id={device} name={query.data?.data[0]?.device_name} onClear={onClearDevice} />}
            {span && <WindowChip span={span} onClear={onClearAround} />}
          </div>
        )}
        <EdrEventsFilters filters={filters} facets={facets} onChange={onFilters} />
        <DataTable
          id={TABLE_ID}
          columns={columns}
          rows={query.data?.data ?? []}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          headerButtons={span ? undefined : <PeriodFilter period={period} onChange={onPeriod} />}
        />
      </div>
    </Content>
  )
}

/** Pastila "Around 09:00 | 08:30 - 09:10 UTC x", in locul perioadei; x intoarce ziua intreaga. */
function WindowChip({ span, onClear }: { span: EdrEventWindow; onClear?(): void }) {
  return (
    <div
      className="xdr-device-filter edr-window-chip"
      role="group"
      aria-label="Time window"
      title={`${span.before} min before, ${span.after} min after (XDR settings > Investigation)`}
    >
      <span className="xdr-device-filter-label">Around {span.around.slice(11, 16)}</span>
      <strong className="xdr-device-filter-name">
        {span.from.slice(11, 16)} - {span.to.slice(11, 16)} UTC
      </strong>
      {onClear && (
        <button type="button" className="xdr-device-filter-clear" title="Show the whole day" onClick={onClear}>
          <span aria-hidden="true">×</span>
          <span className="sr-only">Show the whole day</span>
        </button>
      )}
    </div>
  )
}
